import { useEffect, useMemo, type CSSProperties } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Spin } from 'antd'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../../utils/redux/store'
import {
  attemptGetBusinessesList,
  attemptGetDaytimesList,
  attemptGetMoodsList,
  attemptGetPlaylistsList,
  attemptGetStylesList,
  PlaylistBrief,
  PlaylistFolder,
  PlaylistOrderBy,
} from '../../utils/redux/playlistSlice'
import { OrderDirection } from '../../utils/dto'
import { apiBaseURL } from '../../utils/api'
import { getAccentById, getGradientById } from '../../utils/color'
import '../../components/PlaylistSection.scss'
import './AllCategoryPlaylistsPage.scss'

type RouteType = 'playlist' | 'mood' | 'style' | 'daytime' | 'business'
type UploadedDestination = 'moods' | 'styles' | 'daytimes' | 'businesses'

const TYPE_TO_UPLOADED: Record<Exclude<RouteType, 'playlist'>, UploadedDestination> = {
  mood: 'moods',
  style: 'styles',
  daytime: 'daytimes',
  business: 'businesses',
}

function isRouteType(v: string | undefined): v is RouteType {
  return (
    v === 'playlist' ||
    v === 'mood' ||
    v === 'style' ||
    v === 'daytime' ||
    v === 'business'
  )
}

function asText(value: any, lang = 'en'): string {
  if (value == null) return ''
  if (typeof value === 'string' || typeof value === 'number') return String(value)
  if (Array.isArray(value))
    return value
      .map((x) => asText(x, lang))
      .filter(Boolean)
      .join(' ')

  if (typeof value === 'object') {
    if (value[lang]) return asText(value[lang], lang)
    if (value.en) return asText(value.en, lang)
    if (value.ru) return asText(value.ru, lang)
    if (value.fr) return asText(value.fr, lang)
    const first = Object.values(value).find((v) => typeof v === 'string')
    return first ? String(first) : ''
  }

  return ''
}

function uniqueById<T extends { _id?: string; id?: string }>(arr: T[]): T[] {
  const map = new Map<string, T>()
  const rest: T[] = []

  for (const x of arr) {
    const id = String((x as any)?._id ?? (x as any)?.id ?? '')
    if (!id) rest.push(x)
    else map.set(id, x)
  }

  return [...Array.from(map.values()), ...rest]
}

type NavState = {
  title?: string
  categoryName?: string
  type?: RouteType
  uploadedDestination?: UploadedDestination
  items?: Array<PlaylistFolder | PlaylistBrief>
  folders?: Array<PlaylistFolder | PlaylistBrief>
  playlists?: Array<PlaylistFolder | PlaylistBrief>
}

export default function AllCategoryPlaylistsPage() {
  const { t, i18n } = useTranslation(['home'])
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation() as { state?: NavState }
  const { type } = useParams<{ type: string }>()

  if (!isRouteType(type)) {
    return (
      <section className="acp">
        <div className="acp__invalid">Неверный тип категории.</div>
      </section>
    )
  }

  const { playlistsBrief, moods, styles, daytimes, businesses } = useAppSelector(
    (s) => s.playlist,
  )

  const titleFromState = location.state?.title || location.state?.categoryName
  const pageTitle =
    titleFromState ||
    (type === 'playlist'
      ? t('playlists', { defaultValue: 'Playlists' })
      : type === 'mood'
        ? t('moods', { defaultValue: 'Moods' })
        : type === 'style'
          ? t('styles', { defaultValue: 'Styles' })
          : type === 'daytime'
            ? t('daytimes', { defaultValue: 'Daytimes' })
            : t('businesses', { defaultValue: 'Businesses' }))

  const fromState =
    (location.state?.items as Array<PlaylistFolder | PlaylistBrief> | undefined) ||
    (location.state?.folders as Array<PlaylistFolder | PlaylistBrief> | undefined) ||
    (location.state?.playlists as Array<PlaylistFolder | PlaylistBrief> | undefined) ||
    []

  const fromStore = useMemo(() => {
    if (type === 'playlist')
      return (playlistsBrief ?? []) as Array<PlaylistFolder | PlaylistBrief>
    if (type === 'mood') return (moods ?? []) as Array<PlaylistFolder | PlaylistBrief>
    if (type === 'style') return (styles ?? []) as Array<PlaylistFolder | PlaylistBrief>
    if (type === 'daytime')
      return (daytimes ?? []) as Array<PlaylistFolder | PlaylistBrief>
    return (businesses ?? []) as Array<PlaylistFolder | PlaylistBrief>
  }, [type, playlistsBrief, moods, styles, daytimes, businesses])

  const items = useMemo(() => {
    const src = fromState.length ? fromState : fromStore
    return uniqueById(src)
  }, [fromState, fromStore])

  useEffect(() => {
    if (items.length > 0) return

    const payload = {
      skip: 0,
      limit: 200,
      oderBy: PlaylistOrderBy.TITLE,
      oderDirection: OrderDirection.DESC,
    }

    if (type === 'playlist') dispatch(attemptGetPlaylistsList(payload))
    else if (type === 'mood') dispatch(attemptGetMoodsList(payload))
    else if (type === 'style') dispatch(attemptGetStylesList(payload))
    else if (type === 'daytime') dispatch(attemptGetDaytimesList(payload))
    else dispatch(attemptGetBusinessesList(payload))
  }, [dispatch, type, items.length])

  const uploadedDestination: UploadedDestination | null =
    type === 'playlist'
      ? null
      : (location.state?.uploadedDestination ?? TYPE_TO_UPLOADED[type])

  const getImageUrl = (item: PlaylistFolder | PlaylistBrief) => {
    const raw = String((item as any)?.image ?? '')
    if (/^https?:\/\//i.test(raw)) return raw
    if (!raw) return ''

    if (type === 'playlist') return `${apiBaseURL}/uploaded/playlists/${raw}`
    return `${apiBaseURL}/uploaded/${uploadedDestination}/${raw}`
  }

  const getSubtitleText = (item: any) =>
    asText(
      item?.subtitle ??
        item?.description ??
        (type === 'playlist' ? 'Made for you' : 'Category'),
      i18n.language.split('-')[0],
    )

  const openItem = (item: PlaylistFolder | PlaylistBrief) => {
    const id = String((item as any)?._id ?? (item as any)?.id ?? '')
    if (!id) return

    if (type === 'playlist') {
      navigate(`/playlist/${id}`)
      return
    }

    const name = asText(
      (item as any)?.title ?? (item as any)?.name,
      i18n.language.split('-')[0],
    )
    const image = getImageUrl(item)

    navigate(`/playlist-collection/${type}/${id}`, {
      state: {
        title: name,
        image,
        type,
        uploadedDestination,
      },
    })
  }

  const renderContent = () => {
    if (items.length === 0) {
      return (
        <div className="acp__loading">
          <Spin size="large" />
        </div>
      )
    }

    if (type === 'mood') {
      return (
        <div className="acp-moodGrid ps-moodGrid">
          {items.map((item: any) => {
            const name = asText(item?.title ?? item?.name, i18n.language.split('-')[0])
            const id = String(item?._id ?? item?.id ?? name)
            const acc = getAccentById(id, 'mood')

            return (
              <button
                key={id}
                type="button"
                className="ps-pill"
                style={{ ['--acc' as any]: acc } as CSSProperties}
                onClick={() => openItem(item)}
                title={name}
              >
                {name}
              </button>
            )
          })}
        </div>
      )
    }

    if (type === 'style') {
      return (
        <div className="acp-grid acp-grid--tiles">
          {items.map((item: any) => {
            const id = String(item?._id ?? item?.id ?? '')
            const name = asText(item?.title ?? item?.name, i18n.language.split('-')[0])
            const grad = getGradientById(id || name, 'style')

            // если вдруг есть доп. инфа (можешь удалить, если не нужно)
            const subtitle =
              item?.songsCount != null && Number(item.songsCount) > 0
                ? `${item.songsCount} ${t?.('songs', { ns: 'playlist', defaultValue: 'Songs' }) ?? 'Songs'}`
                : ''

            return (
              <button
                key={id || name}
                type="button"
                className="ps-tile"
                onClick={() => openItem(item)}
                title={name}
                style={
                  {
                    ['--tile-a' as any]: grad.a,
                    ['--tile-b' as any]: grad.b,
                  } as React.CSSProperties
                }
              >
                <div className="ps-tile__label">
                  <div className="ps-tile__title">{name}</div>
                  {subtitle ? <div className="ps-tile__meta">{subtitle}</div> : null}
                </div>

                <div className="ps-tile__artWrap" aria-hidden="true">
                  <img
                    src={getImageUrl(item)}
                    alt={name}
                    className="ps-tile__art"
                    loading="lazy"
                  />
                </div>
              </button>
            )
          })}
        </div>
      )
    }

    return (
      <div className="acp-grid acp-grid--cards">
        {items.map((item: any) => {
          const id = String(item?._id ?? item?.id ?? '')
          const name = asText(item?.title ?? item?.name, i18n.language.split('-')[0])
          const sub = getSubtitleText(item)
          const grad = getGradientById(
            id || name,
            type === 'playlist' ? 'playlist' : type,
          )

          return (
            <button
              key={id || name}
              type="button"
              className="ps-card"
              onClick={() => openItem(item)}
              title={name}
              style={
                {
                  ['--card-a' as any]: grad.a,
                  ['--card-b' as any]: grad.b,
                } as CSSProperties
              }
            >
              <div className="ps-card__imgWrap">
                <img
                  src={getImageUrl(item)}
                  alt={name}
                  className="ps-card__img"
                  loading="lazy"
                />
              </div>
              <div className="ps-card__text">
                <div className="ps-card__name">{name}</div>
                <div className="ps-card__sub">{sub}</div>
              </div>
            </button>
          )
        })}
      </div>
    )
  }

  return (
    <section className={`acp acp--${type}`}>
      <div className="acp__head">
        <button className="acp__back" type="button" onClick={() => navigate(-1)}>
          ←
        </button>
        <h1 className="acp__title">{pageTitle}</h1>
      </div>

      {renderContent()}
    </section>
  )
}
